import React, { useCallback, useRef, useState, useEffect } from 'react';
import ReactFlow, {
    MiniMap,
    Controls,
    Connection,
    Node,
    Edge,
    ReactFlowProvider,
    Background,
    MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import CustomNode from './CustomNode';
import LoopContainerNode from './LoopContainerNode';
import PropertiesPanel from './PropertiesPanel';
import NestedActionsEditor from './NestedActionsEditor';
import { Action, WorkflowVariable } from '../types/workflow';

const nodeTypes = { custom: CustomNode, loopContainer: LoopContainerNode };

function findTailNodeId(nodes: Node[], edges: Edge[]): string | null {
    if (nodes.length === 0) return null;
    const sourceIds = new Set(edges.map(e => e.source));
    const tailNodes = nodes.filter(n => !sourceIds.has(n.id));
    if (tailNodes.length === 0) return null;
    return tailNodes.sort((a, b) => b.position.y - a.position.y)[0].id;
}

interface FlowCanvasProps {
    nodes: Node[];
    edges: Edge[];
    onNodesChange: any;
    onEdgesChange: any;
    onConnect: any;
    onAddNode: (action: Action, position: { x: number; y: number }, connectFromNodeId?: string | null, parentId?: string | null) => void;
    onUpdateNode: (nodeId: string, action: Action) => void;
    onDeleteNode: (nodeId: string) => void;
    onStartInteractive?: (url: string) => void;
    onPickElement?: (nodeId: string, parentLocator?: { method: string; value: string } | null) => void;
    interactiveSessionId?: string | null;
    onPlayAction?: (action: any) => Promise<void>;
    variables?: WorkflowVariable[];
    runtimeVariables?: Record<string, any>;
    onRuntimeVariablesUpdate?: (vars: Record<string, any>) => void;
    onAppendLog?: (level: 'log' | 'error' | 'warn', message: string, nodeLabel?: string) => void;
    workflowId?: string;
}

const FlowCanvasInner: React.FC<FlowCanvasProps> = ({
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onAddNode,
    onUpdateNode,
    onDeleteNode,
    onStartInteractive,
    onPickElement,
    interactiveSessionId,
    onPlayAction,
    variables,
    runtimeVariables,
    onRuntimeVariablesUpdate,
    onAppendLog,
    workflowId,
}) => {
    const reactFlowWrapper = useRef<HTMLDivElement>(null);
    const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
    const [selectedNode, setSelectedNode] = useState<Node | null>(null);
    const [editingNestedActions, setEditingNestedActions] = useState<Node | null>(null);

    // Find which loop container (if any) a flow-space position falls inside
    const findLoopContainerAt = useCallback((flowPos: { x: number; y: number }): string | null => {
        for (const node of nodes) {
            if (node.type !== 'loopContainer') continue;
            const w = node.width ?? 400;
            const h = node.height ?? 300;
            if (
                flowPos.x >= node.position.x &&
                flowPos.x <= node.position.x + w &&
                flowPos.y >= node.position.y &&
                flowPos.y <= node.position.y + h
            ) {
                return node.id;
            }
        }
        return null;
    }, [nodes]);

    // Clear selectedNode if it no longer exists in nodes list (e.g. after deletion)
    useEffect(() => {
        if (selectedNode && !nodes.find(n => n.id === selectedNode.id)) {
            setSelectedNode(null);
        }
    }, [nodes, selectedNode]);

    const onDragOver = useCallback((event: React.DragEvent) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback(
        (event: React.DragEvent) => {
            event.preventDefault();
            const type = event.dataTransfer.getData('application/reactflow');
            if (!type || !reactFlowInstance) return;

            const position = reactFlowInstance.screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });

            const newAction: Action = {
                type: type as any,
                ...(type === 'open_browser' && { url: 'https://' }),
                ...(type === 'sleep' && { duration: 1 }),
                ...((['click', 'hover', 'write', 'get_element', 'get_elements'].includes(type)) && {
                    locator: { method: 'CSS_SELECTOR' as any, value: '' }
                }),
                ...(type === 'write' && { text: '' }),
                ...(type === 'api_call' && { method: 'GET', url: '', headers: {}, body: {} }),
                ...(type === 'loop' && { iterate_over: '', store_current_as: '' }),
                ...((['if', 'while'].includes(type)) && {
                    condition: { type: 'element_exists' as any, locator: { method: 'CSS_SELECTOR' as any, value: '' } },
                    actions: []
                }),
                ...(type === 'switch_to_frame' && { frame_index: 0 }),
            };

            // Loop node: create as a container node, but still auto-connect
            if (type === 'loop') {
                const tailNodeId = findTailNodeId(
                    nodes.filter(n => !n.parentId),
                    edges.filter(e => {
                        const src = nodes.find(n => n.id === e.source);
                        const tgt = nodes.find(n => n.id === e.target);
                        return !src?.parentId && !tgt?.parentId;
                    })
                );
                onAddNode(newAction, position, tailNodeId, null);
                return;
            }

            // Check if dropped inside a loop container
            const loopParentId = findLoopContainerAt(position);

            if (loopParentId) {
                // Position is relative to parent when parentId is set in ReactFlow
                const parentNode = nodes.find(n => n.id === loopParentId);
                const relativePos = parentNode
                    ? { x: position.x - parentNode.position.x, y: position.y - parentNode.position.y }
                    : position;
                onAddNode(newAction, relativePos, null, loopParentId);
            } else {
                const tailNodeId = findTailNodeId(
                    nodes.filter(n => !n.parentId),
                    edges.filter(e => {
                        const src = nodes.find(n => n.id === e.source);
                        const tgt = nodes.find(n => n.id === e.target);
                        return !src?.parentId && !tgt?.parentId;
                    })
                );
                onAddNode(newAction, position, tailNodeId, null);
            }
        },
        [reactFlowInstance, onAddNode, nodes, edges, findLoopContainerAt]
    );

    const handleNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
        const action = node.data.action;
        if (action.type === 'while' || action.type === 'if') {
            setEditingNestedActions(node);
        } else {
            setSelectedNode(node);
        }
    }, []);

    const handleUpdateNestedActions = useCallback((nodeId: string, updatedAction: Action) => {
        onUpdateNode(nodeId, updatedAction);
    }, [onUpdateNode]);

    const handleConnect = useCallback(
        (params: Connection) => {
            const newEdge = {
                ...params,
                type: 'smoothstep',
                animated: true,
                markerEnd: { type: MarkerType.ArrowClosed },
            };
            onConnect(newEdge);
        },
        [onConnect]
    );

    const handleClosePanel = useCallback(() => {
        setSelectedNode(null);
    }, []);

    return (
        <div ref={reactFlowWrapper} style={{ height: '100%', width: '100%' }}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={handleConnect}
                onInit={setReactFlowInstance}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onNodeClick={handleNodeClick}
                nodeTypes={nodeTypes}
                fitView
                deleteKeyCode={['Backspace', 'Delete']}
                defaultEdgeOptions={{
                    type: 'smoothstep',
                    animated: true,
                    markerEnd: { type: MarkerType.ArrowClosed },
                    style: { strokeWidth: 2 },
                }}
                nodesDraggable
                nodesConnectable
            >
                <Background />
                <MiniMap />
                <Controls />
            </ReactFlow>
            {editingNestedActions && (
                <NestedActionsEditor
                    parentAction={editingNestedActions.data.action}
                    onUpdate={(updatedAction) => {
                        handleUpdateNestedActions(editingNestedActions.id, updatedAction);
                        setEditingNestedActions(null);
                    }}
                    onClose={() => setEditingNestedActions(null)}
                />
            )}
            {selectedNode && !editingNestedActions && (
                <PropertiesPanel
                    selectedNode={nodes.find(n => n.id === selectedNode.id) || selectedNode}
                    onUpdate={onUpdateNode}
                    onClose={handleClosePanel}
                    onStartInteractive={onStartInteractive}
                    onPickElement={onPickElement}
                    onPlayAction={onPlayAction}
                    interactiveSessionId={interactiveSessionId}
                    variables={variables}
                    runtimeVariables={runtimeVariables}
                    onRuntimeVariablesUpdate={onRuntimeVariablesUpdate}
                    onAppendLog={onAppendLog}
                    workflowId={workflowId}
                />
            )}
        </div>
    );
};

const FlowCanvas: React.FC<FlowCanvasProps> = (props) => {
    return (
        <ReactFlowProvider>
            <FlowCanvasInner {...props} />
        </ReactFlowProvider>
    );
};

export default FlowCanvas;